
import { foods, symptoms } from '../database/food-database';
import { FoodItem, SymptomItem, Language } from '../database/db-types';

// ============================================================================
// HELPERS
// ============================================================================

const stripAccents = (str: string) => 
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const levenshtein = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) matrix[i][j] = matrix[i - 1][j - 1];
      else matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1));
    }
  }
  return matrix[b.length][a.length];
};

// ============================================================================
// SEARCH LOGIC
// ============================================================================

const searchDatabase = <T extends { en: string; fr: string; keywords?: string[] }>(
  query: string,
  dataset: T[],
  lang: Language,
  limit: number
): string[] => {
  const lowerQuery = stripAccents(query.toLowerCase().trim());
  if (!lowerQuery) return [];

  // CONSERVATIVE THRESHOLD: No errors for words <= 5, strictly limited for longer
  const maxDistance = lowerQuery.length <= 5 ? 0 : lowerQuery.length <= 8 ? 1 : 2;

  const matches = dataset.map(item => {
    const name = item[lang];
    const normalizedName = stripAccents(name.toLowerCase());
    let score = 0;

    if (normalizedName === lowerQuery) score = 100;
    else if (normalizedName.startsWith(lowerQuery)) score = 80;
    else if (normalizedName.includes(lowerQuery) && lowerQuery.length > 5) score = 60;
    else if (item.keywords) {
        if (item.keywords.some(k => stripAccents(k.toLowerCase()) === lowerQuery)) score = 70;
        else if (item.keywords.some(k => stripAccents(k.toLowerCase()).startsWith(lowerQuery))) score = 65;
        else if (lowerQuery.length > 5 && item.keywords.some(k => stripAccents(k.toLowerCase()).includes(lowerQuery))) score = 50;
    }

    if (score === 0) {
        const dist = levenshtein(lowerQuery, normalizedName);
        if (dist <= maxDistance && dist > 0) score = 40 - (dist * 15); 
        else if (item.keywords) {
            for (const k of item.keywords) {
                const kDist = levenshtein(lowerQuery, stripAccents(k.toLowerCase()));
                if (kDist <= maxDistance && kDist > 0) score = Math.max(score, 35 - (kDist * 15));
            }
        }
    }
    return { name, score };
  })
  .filter(m => m.score > 0)
  .sort((a, b) => b.score - a.score);

  const uniqueNames = new Set<string>();
  const results: string[] = [];
  for (const m of matches) {
      if (!uniqueNames.has(m.name)) {
          uniqueNames.add(m.name);
          results.push(m.name);
          if (results.length >= limit) break;
      }
  }
  return results;
};

// ============================================================================
// EXPORTS
// ============================================================================

export const getFoodList = (lang: Language): string[] => foods.map(f => f[lang]);
export const getSymptomList = (lang: Language): string[] => symptoms.map(s => s[lang]);
export const searchFoods = (query: string, lang: Language = 'en', limit: number = 10): string[] => searchDatabase(query, foods, lang, limit);
export const searchSymptoms = (query: string, lang: Language = 'en', limit: number = 10): string[] => searchDatabase(query, symptoms, lang, limit);

export const getFoodNameById = (id: string, lang: Language): string | null => {
    const food = foods.find(f => f.id === id);
    return food ? food[lang] : null;
};

export const normalizeSymptom = (query: string, lang: Language): string | null => {
    const q = stripAccents(query.toLowerCase().trim());
    const exactMatch = symptoms.find(s => stripAccents(s[lang].toLowerCase()) === q);
    if (exactMatch) return exactMatch[lang];
    const results = searchDatabase(query, symptoms, lang, 1);
    return results.length > 0 ? results[0] : null;
};

export const normalizeFood = (query: string, lang: Language): string | null => {
    const q = stripAccents(query.toLowerCase().trim());
    const exactMatch = foods.find(f => stripAccents(f[lang].toLowerCase()) === q);
    if (exactMatch) return exactMatch[lang];
    const results = searchDatabase(query, foods, lang, 1);
    return results.length > 0 ? results[0] : null;
};

export const searchItems = (query: string, items: string[], limit: number = 10): string[] => {
    const lowerQuery = stripAccents(query.toLowerCase().trim());
    if (!lowerQuery) return [];
    const maxDistance = lowerQuery.length <= 5 ? 0 : 1;
    return items.map(item => {
            const lowerItem = stripAccents(item.toLowerCase());
            let score = 0;
            if (lowerItem === lowerQuery) score = 100;
            else if (lowerItem.startsWith(lowerQuery)) score = 80;
            else if (lowerItem.includes(lowerQuery) && lowerQuery.length > 5) score = 60;
            else {
                const dist = levenshtein(lowerQuery, lowerItem);
                if (dist <= maxDistance && dist > 0) score = 40 - (dist * 15);
            }
            return { item, score };
        })
        .filter(x => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .map(x => x.item)
        .slice(0, limit);
};
